import { Link } from 'lucide-react'

interface DocsHeadingProps {
  as?: 'h2' | 'h3' | 'h4'
  id: string
  children: React.ReactNode
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export function DocsHeading({ as: Component = 'h2', id, children }: DocsHeadingProps) {
  const headingId = id || (typeof children === 'string' ? slugify(children) : '')

  const handleClick = () => {
    const url = `${window.location.pathname}#${headingId}`
    navigator.clipboard.writeText(window.location.origin + url)
    window.location.hash = headingId
  }

  const baseStyles = 'group relative scroll-mt-20'
  const componentStyles = {
    h2: 'text-xl md:text-2xl font-bold text-foreground mt-12 mb-4 pb-3 border-b border-border',
    h3: 'text-lg md:text-xl font-semibold text-foreground mt-8 mb-3',
    h4: 'text-base md:text-lg font-medium text-foreground mt-6 mb-2',
  }

  return (
    <Component id={headingId} className={`${baseStyles} ${componentStyles[Component]}`}>
      <a
        href={`#${headingId}`}
        onClick={e => {
          e.preventDefault()
          handleClick()
        }}
        className="absolute -left-6 top-1/2 hidden -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-amber-500 md:block"
        aria-label={`Link to ${children}`}
      >
        <Link className="h-4 w-4" />
      </a>
      {children}
    </Component>
  )
}

// Convenience exports for different heading levels
export function H2({ id, children }: Omit<DocsHeadingProps, 'as'>) {
  return (
    <DocsHeading as="h2" id={id}>
      {children}
    </DocsHeading>
  )
}

export function H3({ id, children }: Omit<DocsHeadingProps, 'as'>) {
  return (
    <DocsHeading as="h3" id={id}>
      {children}
    </DocsHeading>
  )
}

export function H4({ id, children }: Omit<DocsHeadingProps, 'as'>) {
  return (
    <DocsHeading as="h4" id={id}>
      {children}
    </DocsHeading>
  )
}
